import { useState } from "react";
import Icon from "@mdi/react";
import { mdiComment, mdiPencil, mdiDelete } from "@mdi/js";
import { useLang } from "../../../services/lang";
import { useUser } from "../../../components/User";
import { api, handleError } from "../../../services/api";
import { Accordion, ConfirmButton, VoteResult, When } from "../../../components";
import Dialog from "../../../components/Dialog";
import { Input } from "../../../components/Form";
import { Votes, VoteUsers } from "../../../components/Vote";
import { ID, Vote, VoteFull, TeamEvent, Team } from "../../../../types";
import { getUserPermissions } from "../utils";

type TeamEventDetailProps = {
    teamId: ID;
    isActive: boolean;
    userHashId: string;
    users: Team["users"];
    event: TeamEvent & { date: string };
    onEditEvent: (date?: string) => void;
    onChange: () => void;
};

export function TeamEventDetail({ teamId, isActive, userHashId, users, event, onEditEvent, onChange }: TeamEventDetailProps) {
    const { dict } = useLang();
    const { userId, user } = useUser();
    const [active, setActive] = useState<Vote>("yes");

    const [showCommentDialog, setShowCommentDialog] = useState<null | { comment: string }>(null);

    const { isTeamMember, isTeamAdmin } = getUserPermissions(userHashId, users);
    const userVote = event.users[userHashId] || { vote: "", comment: "" };

    const usersVotes = Object.values(event.users);
    const votes = {
        yes: usersVotes.filter(v => v.vote === "yes").length,
        no: usersVotes.filter(v => v.vote === "no").length,
        maybe: usersVotes.filter(v => v.vote === "maybe").length,
    };

    async function handleVote(date: string, vote: VoteFull) {
        if (!isTeamMember) return;

        setActive(vote.vote);

        const [_, error] = await api.updateTeamVote({ teamId, userId, user, date, vote });

        if (error) {
            handleError(error);
        } else {
            onChange();
        }
    }

    async function handleDeleteEvent() {
        if (!isTeamAdmin) return;

        const [, error] = await api.removeTeamEvent({ teamId, date: event.date });

        if (error) {
            handleError(error);
        } else {
            onChange();
        }
    }

    return <li className={`poll-option`}>
        <Accordion
            title={<VoteResult
                title={<>
                    <div className="votes">
                        <Votes
                            isMaybeOption
                            active={userVote.vote || "nothing"}
                            votes={votes}
                            onClick={isActive ? v => handleVote(event.date, { ...userVote, vote: v }) : undefined}
                        />
                    </div>
                    <When date={event.date} />
                </>}
                yes={votes.yes}
                no={votes.no}
                maybe={votes.maybe}
            />} >

            {isActive && userVote.vote && <button className="team-comment-button" onClick={() => setShowCommentDialog({ comment: userVote.comment })}>
                <Icon path={mdiComment} size={1} />{dict.teamEventComment}
            </button>}
            {isTeamAdmin && <div className="team-admin-buttons">
                <button onClick={() => onEditEvent(event.date)}>
                    <Icon path={mdiPencil} size={1} />
                </button>
                <ConfirmButton text={dict.teamEventRemoveConfirm} onClick={handleDeleteEvent}>
                    <Icon path={mdiDelete} size={1} />
                </ConfirmButton>
            </div>}

            {event.description && <p className="team-event-description pre-line">{event.description}</p>}
            <VoteUsers users={users} votes={event.users} isMaybeOption active={active} onChange={setActive} />
        </Accordion>

        <Dialog show={showCommentDialog !== null} onClose={() => setShowCommentDialog(null)}>
            <h2>{dict.teamEventComment}</h2>
            <p><When date={event.date} /></p>
            {showCommentDialog && <>
                <Votes
                    isMaybeOption
                    active={userVote.vote || "maybe"}
                    votes={votes}
                    onClick={isActive ? v => handleVote(event.date, { ...showCommentDialog, vote: v }) : undefined}
                />
                <Input type="textarea" label="Komentář (volitelně)" field="comment" maxLength={400} value={showCommentDialog} setValue={setShowCommentDialog} />
                <button onClick={() => {
                    handleVote(event.date, { vote: userVote.vote || "maybe", ...showCommentDialog });
                    setShowCommentDialog(null);
                }}>{dict.teamEventCommentSave}</button>
            </>}
        </Dialog>
    </li>;
}
